// src/utils/addressExport.ts
// Shared CSV export for any list of CreationAddress / WorkloadAddress objects.
// Used by every workload-creation discovery mode (saturation, census, draw area,
// CSV upload merge step) plus the workload detail page.
//
// Columns are chosen to round-trip with csvService.parse() auto-detection in
// CsvUploadHandler so a CSV exported here can be re-uploaded as the source of
// a new workload without manual column mapping.

import { format } from 'date-fns';
import type { CreationAddress } from '../store/workloadStore';

export type AddressSource =
  | 'saturation'
  | 'census_block'
  | 'draw_area'
  | 'csv_upload'
  | 'walking_pattern'
  | 'unknown';

export interface ExportableAddress {
  street: string;
  unit?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  location?: { lat?: number; lng?: number };
  source?: AddressSource;
}

const escapeCell = (value: string | number | undefined | null): string => {
  const str = value == null ? '' : String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

/**
 * Export an array of addresses to a CSV file and trigger a browser download.
 * The column set is intentionally compatible with the CSV upload flow's
 * auto-detection (Street, Unit, City, State, ZIP) so exported lists can be
 * re-imported into a new workload.
 */
export const exportAddressesToCSV = (
  addresses: ExportableAddress[],
  options: {
    filename?: string;
    source?: AddressSource;
  } = {}
): void => {
  if (!addresses || addresses.length === 0) {
    console.warn('exportAddressesToCSV called with empty list');
    return;
  }

  const headers = [
    'Street',
    'Unit',
    'City',
    'State',
    'ZIP',
    'Latitude',
    'Longitude',
    'Source',
    'Discovered At',
  ];

  const stamp = format(new Date(), 'yyyy-MM-dd HH:mm');
  const defaultSource = options.source || 'unknown';

  const rows = addresses.map((addr) => [
    addr.street || '',
    addr.unit || '',
    addr.city || '',
    addr.state || '',
    addr.zipCode || '',
    addr.location?.lat ?? '',
    addr.location?.lng ?? '',
    addr.source || defaultSource,
    stamp,
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.map(escapeCell).join(',')),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const filename =
    options.filename ||
    `workload-addresses-${format(new Date(), 'yyyy-MM-dd-HHmm')}.csv`;

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Convenience helper for the most common case: a workload-creation address list.
export const exportCreationAddressesToCSV = (
  addresses: CreationAddress[],
  source: AddressSource = 'unknown',
  filename?: string
): void => {
  exportAddressesToCSV(
    addresses.map((a) => ({
      street: a.street,
      unit: a.unit,
      city: a.city,
      state: a.state,
      zipCode: a.zipCode,
      location: a.location,
      source,
    })),
    { filename, source }
  );
};
